import { useTranslation } from 'react-i18next';

import {
  TableChartConfigDto,
  PitcherSingleSessionPitchByPitchStatsChartDataDto,
  PitcherSingleSessionPitchByPitchStatsDto,
} from 'types/CodegenReportTypes';

import { ChartProps } from 'utilities/chartIdToComponent';
import { useMemo, useState } from 'react';
import Table, { Order } from 'components/Table/Table';
import { getDisplayedColumns, tableHeaders } from 'utilities/tableHeaders';
import PitchTypeCell from 'components/TableCells/PitchTypeCell';
import PitchLocationCell from 'components/TableCells/PitchLocationCell';
import useReportStore from 'hooks/SessionReport/PitcherSingle/reportStore';

export type PitcherSingleSessionPitchByPitchStatsChartColumn =
  keyof PitcherSingleSessionPitchByPitchStatsChartDataDto['pitchByPitchStats'][number];

const PitcherSingleSessionPitchByPitchStatsChart = ({
  chartData,
  isLoading,
  extraChartData,
}: ChartProps<PitcherSingleSessionPitchByPitchStatsChartDataDto, TableChartConfigDto>) => {
  const { t } = useTranslation();

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<PitcherSingleSessionPitchByPitchStatsChartColumn | '' | 'id'>('');

  const { selectedConfgValues } = useReportStore();

  const rows = useMemo(
    () =>
      chartData?.pitchByPitchStats.map((row, i) => ({
        ...row,
        id: i.toString(),
      })) ?? [],
    [chartData?.pitchByPitchStats]
  );

  const columns = useMemo(() => {
    const displayedColumns = getDisplayedColumns<PitcherSingleSessionPitchByPitchStatsChartColumn>(extraChartData.visibleStats);
    const pitchTypeColumnNameKey = selectedConfgValues.pitchGrouping === 'Types' ? 'pitchType' : 'parentPitchType';

    return displayedColumns.map((column) => ({
      ...tableHeaders[column],
      field: column,
      headerName: column === 'pitchType' ? t(pitchTypeColumnNameKey) : t(column),
      headerShortName: column === 'pitchType' ? t('short.' + pitchTypeColumnNameKey) : t('short.' + column),
      sortable: true,
      renderCell:
        column === 'pitchCall'
          ? (cellValue) => t(cellValue)
          : column === 'pitchLocation'
            ? (cellValue, row: PitcherSingleSessionPitchByPitchStatsDto) => (
                <PitchLocationCell pitchLocation={cellValue} pitchType={row.pitchType} playerType='Pitcher' />
              )
            : column === 'pitchType'
              ? (cellValue) => <PitchTypeCell pitchType={cellValue} />
              : undefined,
    }));
  }, [extraChartData.visibleStats, selectedConfgValues.pitchGrouping]);

  return (
    <Table
      rows={rows}
      pageState={[page, setPage]}
      orderState={[order, setOrder]}
      orderByState={[orderBy, setOrderBy]}
      columns={columns}
      loading={isLoading}
      noFooter
      tileTable
    />
  );
};

export default PitcherSingleSessionPitchByPitchStatsChart;
